<script lang="ts" setup>
import { Components } from '@/api/generated/api';
import { TSchoolSubjectWithGroupId } from '@/composables/schoolSubjectGroups/useSchoolSubjectGroup';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import CustomAccordion from '@/core/elements/CustomAccordion.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SearchInput from '@/core/elements/SearchInput.vue';
import SchoolSubjectSearchSelectList from '@/core/school-subjects/SchoolSubjectSearchSelectList.vue';
import { ISelectOption } from '@/interfaces';
import { getSchoolSubjectGroupsWithSubjects } from '@/utils/schoolSubject.utils';
import { matches } from 'common/utils/string.utils';
import { useDebounce } from '@vueuse/core';
import { EIcon, EPosition } from 'common/enum/misc.enum';
import { immutableSort, toRecord } from 'common/utils/array.utils';
import { ComponentPublicInstance, computed, nextTick, ref, toRefs, useTemplateRef } from 'vue';
import LightBulbIcon from '@/assets/icons/LightBulbIcon.vue';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { useRouter } from 'vue-router';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import CustomSeparator from '../elements/CustomSeparator.vue';
import { usePermissions } from '@/composables/usePermissions';

const emit = defineEmits<{
  'update:modelValue': [value: number[]];
}>();

const props = defineProps<{
  anchorElement?: HTMLElement | null;
  availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
  allSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  modelValue: number[];
  canSelectMultiple?: boolean;
  fullWidth?: boolean;
  searchPlaceholder?: string;
}>();
const { availableSchoolSubjectGroups, allSchoolSubjects, modelValue, anchorElement } = toRefs(props);

const { push } = useRouter();
const { currentClass } = useCurrentClass();
const { canManageCompetences } = usePermissions();
const menuDialogRef = useTemplateRef('menuDialogRef');
const accordionRefs = ref<InstanceType<typeof CustomAccordion>[]>([]);

const searchQuery = ref('');
const debouncedSearchQuery = useDebounce(searchQuery, 300);

const closeMenu = (): void => {
  menuDialogRef.value?.hide();
};

const reset = (): void => {
  searchQuery.value = '';
};

const isOpen = computed(() => !!menuDialogRef.value?.isOpen);

const sortedSchoolSubjectGroups = computed<Components.Schemas.SchoolSubjectGroupDto[]>(() => {
  return immutableSort(
    getSchoolSubjectGroupsWithSubjects(availableSchoolSubjectGroups.value, allSchoolSubjects.value),
    (a, b) => a.order - b.order,
  );
});

const schoolSubjectOptionsWithoutGroup = computed<ISelectOption<Components.Schemas.SchoolSubjectDto>[]>(() => {
  return allSchoolSubjects.value
    .filter((schoolSubject) => !schoolSubject.groupId)
    .sort((a, b) => a.order - b.order)
    .map((s) => ({
      label: s.name,
      value: s,
    }));
});

const filteredUngroupedOptions = computed<ISelectOption<Components.Schemas.SchoolSubjectDto>[]>(() => {
  const query = debouncedSearchQuery.value.trim().toLowerCase();
  const base = schoolSubjectOptionsWithoutGroup.value;

  if (!query) return base;
  return base.filter((o) => matches(o.label, query));
});

const schoolSubjectOptionsByGroupId = computed<Record<number, ISelectOption<TSchoolSubjectWithGroupId>[]>>(() => {
  const schoolSubjectsWithGroup = allSchoolSubjects.value
    .filter((schoolSubject): schoolSubject is TSchoolSubjectWithGroupId => !!schoolSubject.groupId)
    .sort((a, b) => a.order - b.order);

  return toRecord(
    schoolSubjectsWithGroup,
    (schoolSubject) => schoolSubject.groupId,
    (schoolSubject, existing) => [...(existing ?? []), { label: schoolSubject.name, value: schoolSubject }],
  );
});

const selectOption = (value: number[]): void => {
  emit('update:modelValue', value);
  if (!props.canSelectMultiple) menuDialogRef.value?.hide();
};

const filteredSchoolSubjectGroups = computed(() => {
  const query = debouncedSearchQuery.value.trim().toLowerCase();

  return sortedSchoolSubjectGroups.value.filter((group) => {
    const options = schoolSubjectOptionsByGroupId.value[group.id] ?? [];

    if (!query) return options.length > 0;
    return options.some((o) => matches(o.label, query));
  });
});

const routeToSchoolSubjects = async (): Promise<void> => {
  assertNotNullOrUndefined(currentClass.value);

  await push({
    name: '//class/[classId]/settings/schoolSubjects/',
    params: { classId: currentClass.value.id },
  });
};

const setAccordionRef = (el: Element | ComponentPublicInstance | null, groupId: number): void => {
  if (!el) return;
  accordionRefs.value[groupId] = el as InstanceType<typeof CustomAccordion>;
};

const hasResults = computed(() => {
  return filteredUngroupedOptions.value.length > 0 || filteredSchoolSubjectGroups.value.length > 0;
});

const showMenu = async (): Promise<void> => {
  menuDialogRef.value?.show();
  const firstGroup = sortedSchoolSubjectGroups.value[0];
  if (sortedSchoolSubjectGroups.value.length && accordionRefs.value[firstGroup.id]) {
    accordionRefs.value[firstGroup.id].show();
  }
  await updateDialogPosition();
};

const toggleMenu = async (): Promise<void> => {
  if (isOpen.value) {
    closeMenu();
    return;
  }

  await showMenu();
};

const updateDialogPosition = async (): Promise<void> => {
  await nextTick();
  menuDialogRef.value?.updateDialogPosition();
};

defineExpose({
  showMenu,
  toggleMenu,
  isOpen,
});
</script>

<template>
  <PositionablePopover
    ref="menuDialogRef"
    scroll-mode="content"
    :anchor-element="anchorElement"
    :auto-flip="false"
    :position="EPosition.BOTTOM_RIGHT"
    :full-width="fullWidth"
    flip-strategy="vertical"
    @hide="reset"
  >
    <div :class="[!fullWidth && 'w-[360px]', 'flex min-h-0 max-w-full flex-col overflow-hidden rounded-md']">
      <div ref="searchInputRef" class="min-w-0 shrink-0 px-md pt-md pb-sm">
        <SearchInput
          v-if="allSchoolSubjects.length"
          v-model="searchQuery"
          class="w-full min-w-0"
          size="md"
          :placeholder="searchPlaceholder ?? $t('schoolSubjects.placeholder')"
          :label="$t('schoolSubjects.label')"
          :icon-name="EIcon.SEARCH"
        />
      </div>
      <div ref="menuRef" class="min-h-0 flex-1 rounded-md overflow-y-auto overscroll-contain max-h-[400px] py-sm">
        <SchoolSubjectSearchSelectList
          v-if="schoolSubjectOptionsWithoutGroup.length"
          class="pr-md pl-[41px]"
          :can-select-multiple="canSelectMultiple"
          :model-value="modelValue"
          :options="schoolSubjectOptionsWithoutGroup"
          :search-query="debouncedSearchQuery"
          @close-menu="closeMenu"
          @update:model-value="selectOption"
        />
        <CustomAccordion
          v-for="group in filteredSchoolSubjectGroups"
          :key="group.id"
          :ref="(el) => setAccordionRef(el, group.id)"
          :identifier="`school-subject-search-select-${group.id}`"
          button-style="!bg-transparent"
          @update:expanded="updateDialogPosition"
        >
          <template #header>
            <span class="text-body font-semibold text-left">{{ group.name }}</span>
          </template>
          <template #items>
            <SchoolSubjectSearchSelectList
              class="pr-md pl-[41px]"
              :can-select-multiple="canSelectMultiple"
              :model-value="modelValue"
              :options="schoolSubjectOptionsByGroupId[group.id]"
              :search-query="debouncedSearchQuery"
              @close-menu="closeMenu"
              @update:model-value="selectOption"
            />
          </template>
        </CustomAccordion>
        <EmptyPlaceholder
          v-if="!hasResults && (allSchoolSubjects.length || !canManageCompetences)"
          :data="[]"
          :message="$t('general.noResults')"
        />
        <div v-if="!allSchoolSubjects.length && canManageCompetences" class="flex flex-col gap-sm px-md">
          <div class="flex items-start gap-sm text-secondary leading-[27px]">
            <LightBulbIcon class="flex-shrink-0 w-icon-md h-icon-md" />
            {{ $t('performanceAssessmentEntry.noSubjectsYet') }}
          </div>
          <CustomSeparator class="mx-0" />
          <div class="dropdown-option" @click="routeToSchoolSubjects">
            {{ $t('performanceAssessmentEntry.editCompetenceGrid') }}
          </div>
        </div>
      </div>
    </div>
  </PositionablePopover>
</template>
